import React, { useState, useEffect, useRef } from 'react';
import { Search, X, ChevronRight, Check } from 'lucide-react';
import api from '../lib/api';

const CATEGORY_ICONS = {
  'Fashion & Apparel': '👗',
  'Beauty & Personal Care': '💄',
  'Handicrafts': '🎨',
  'Home & Living': '🏠',
  'Furniture': '🪑',
  'Jewellery': '💍',
  'Electronics': '📱',
  'Kitchen & Dining': '🍽️',
  'Kids & Baby': '👶',
  'Food & Grocery': '🌿',
  'Bags & Accessories': '👜',
  'Footwear': '👟',
  'Other': '📦',
};

export const CategorySelector = ({ value, subcategoryValue, onSelect, onClose }) => {
  const [search, setSearch] = useState('');
  const [categories, setCategories] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    fetchCategories('');
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const fetchCategories = async (q) => {
    setLoading(true);
    try {
      const res = await api.get(`/categories${q ? `?q=${encodeURIComponent(q)}` : ''}`);
      setCategories(res.data.categories || []);
    } catch {}
    setLoading(false);
  };

  const handleSearch = (q) => {
    setSearch(q);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchCategories(q), 250);
  };

  const selectCategory = (cat, sub) => {
    onSelect(cat, sub || '');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-t-2xl sm:rounded-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="category-selector">
        {/* Header */}
        <div className="flex items-center gap-3 p-4 border-b border-slate-200">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
            <input ref={inputRef} value={search} onChange={e => handleSearch(e.target.value)}
              placeholder="Search categories..." autoFocus
              className="w-full h-10 pl-9 pr-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 outline-none focus:border-purple-400"
              data-testid="category-search-input" />
          </div>
          <button onClick={onClose} className="p-2 text-slate-400"><X size={20} /></button>
        </div>

        {/* Current Selection */}
        {value && (
          <div className="px-4 py-2 bg-purple-50 border-b border-purple-100 flex items-center gap-2">
            <Check size={14} className="text-purple-600" />
            <span className="text-xs text-purple-700 font-medium">{value}{subcategoryValue ? ` → ${subcategoryValue}` : ''}</span>
          </div>
        )}

        {/* Categories */}
        <div className="flex-1 overflow-y-auto p-2" data-testid="category-list">
          {loading && <p className="text-center text-xs text-slate-400 py-4">Searching...</p>}
          {!loading && categories.length === 0 && search && (
            <div className="text-center py-8">
              <p className="text-sm text-slate-500">No categories found</p>
              <button onClick={() => { onSelect(search, ''); onClose(); }}
                className="mt-3 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg text-xs font-medium"
                data-testid="use-custom-category">
                Use "{search}" as custom category
              </button>
            </div>
          )}
          {categories.map(cat => (
            <div key={cat.name} className="mb-1">
              <button onClick={() => {
                if (cat.subcategories?.length > 0) setExpanded(expanded === cat.name ? null : cat.name);
                else selectCategory(cat.name, '');
              }}
                className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-colors ${expanded === cat.name ? 'bg-purple-50' : 'active:bg-slate-50'} ${value === cat.name && !subcategoryValue ? 'ring-2 ring-purple-300' : ''}`}
                data-testid={`cat-${cat.name}`}>
                <span className="text-xl flex-shrink-0">{CATEGORY_ICONS[cat.name] || '📦'}</span>
                <span className="text-sm font-medium text-slate-700 flex-1">{cat.name}</span>
                {cat.subcategories?.length > 0 && <ChevronRight size={16} className={`text-slate-400 transition-transform ${expanded === cat.name ? 'rotate-90' : ''}`} />}
              </button>
              {/* Subcategories */}
              {expanded === cat.name && cat.subcategories?.length > 0 && (
                <div className="ml-10 space-y-0.5 mt-1 mb-2">
                  {/* Select main category without sub */}
                  <button onClick={() => selectCategory(cat.name, '')}
                    className={`w-full text-left px-3 py-2 rounded-lg text-xs text-slate-500 active:bg-purple-50 ${value === cat.name && !subcategoryValue ? 'bg-purple-50 text-purple-700 font-medium' : ''}`}>
                    All {cat.name}
                  </button>
                  {cat.subcategories.map(sub => (
                    <button key={sub} onClick={() => selectCategory(cat.name, sub)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-xs active:bg-purple-50 transition-colors ${value === cat.name && subcategoryValue === sub ? 'bg-purple-100 text-purple-700 font-medium' : 'text-slate-600'}`}
                      data-testid={`subcat-${sub}`}>
                      {sub}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
